import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { GameCard } from '@/components/common/GameCard';
import { VideoTheater } from '@/components/common/VideoTheater';
import { SEO } from '@/components/common/SEO';
import { Trophy, ChevronRight } from 'lucide-react';

const REGIONS = {
  west:    { slug: 'west',    name: 'WPSL West',    label: 'West' },
  east:    { slug: 'east',    name: 'WPSL East',    label: 'East' },
  central: { slug: 'central', name: 'WPSL Central', label: 'Central' },
  south:   { slug: 'south',   name: 'WPSL South',   label: 'South' },
};

export const RegionPage = ({ leagues = [] }) => {
  const { slug } = useParams();
  const region = REGIONS[slug];
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  const conferences = useMemo(
    () => leagues.filter(l => l.region === slug).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [leagues, slug]
  );

  useEffect(() => {
    if (!region || conferences.length === 0) { setLoading(false); return; }
    let alive = true;
    const load = async () => {
      try {
        // Parallel-fetch games for all conferences in this region, then merge
        const responses = await Promise.all(
          conferences.map(c => api.getGames({ league_id: c.id, limit: 100 }))
        );
        if (!alive) return;
        const merged = responses.flatMap(r => r.data || []);
        merged.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        setGames(merged);
      } catch (e) {
        console.error('Region load failed:', e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [region, conferences]);

  if (!region) return <Navigate to="/" replace />;

  const now = Date.now();
  const upcoming = games.filter(g => new Date(g.scheduled_time).getTime() >= now);
  const replays = games.filter(g => new Date(g.scheduled_time).getTime() < now);

  return (
    <div data-testid={`region-page-${slug}`}>
      <SEO
        title={region.name}
        description={`${region.name} — conferences, upcoming matches, and replays across the ${region.label} region of the WPSL.`}
        url={`/region/${slug}`}
      />

      {/* Hero strip */}
      <div
        className="relative overflow-hidden border-b border-wpsl-blue/30"
        style={{ background: 'linear-gradient(135deg, #cb171e 0%, #001428 55%, #0064a6 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-14 relative">
          <div className="text-xs uppercase tracking-[0.35em] text-white/70 mb-3">Region</div>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-none"
            style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.01em' }}
          >
            {region.name}
          </h1>
          <p className="text-white/80 mt-3 max-w-2xl">
            {conferences.length} conference{conferences.length === 1 ? '' : 's'} • {games.length} match{games.length === 1 ? '' : 'es'} tracked
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 space-y-12">
        {/* Conferences grid */}
        <section data-testid="region-conferences">
          <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Conferences
          </h2>
          {conferences.length === 0 ? (
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-8 text-center text-gray-400">
              No conferences configured yet for this region.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {conferences.map(conf => (
                <Link
                  key={conf.id}
                  to={`/league/${conf.slug}`}
                  className="group bg-gradient-to-br from-[#001428] to-[#000814] border border-wpsl-blue/30 hover:border-wpsl-red/60 rounded-xl p-5 flex items-center gap-4 transition-colors"
                  data-testid={`conference-card-${conf.slug}`}
                >
                  {conf.logo ? (
                    <img
                      src={resolveMediaUrl(conf.logo)}
                      alt={conf.name}
                      className="h-14 w-14 object-contain bg-white/5 rounded-lg p-1.5 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-wpsl-blue/15 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-7 h-7 text-wpsl-blue" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-wpsl-red font-bold">
                      Conference
                    </div>
                    <div className="text-white font-semibold truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {conf.name}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Upcoming */}
        <section data-testid="region-upcoming">
          <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Upcoming in {region.label}
          </h2>
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : upcoming.length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming matches scheduled.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {upcoming.slice(0, 12).map(g => (
                <GameCard key={g.id} game={g} onWatch={setSelectedGame} />
              ))}
            </div>
          )}
        </section>

        {/* Replays */}
        {replays.length > 0 && (
          <section data-testid="region-replays">
            <h2 className="text-2xl font-bold text-white mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Replays
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {replays.slice(0, 12).map(g => (
                <GameCard key={g.id} game={g} onWatch={setSelectedGame} />
              ))}
            </div>
          </section>
        )}
      </div>

      <VideoTheater game={selectedGame} isOpen={!!selectedGame} onClose={() => setSelectedGame(null)} />
    </div>
  );
};

export default RegionPage;
